import React, { useState, useEffect } from "react";
import { useData } from "@/contexts/DataContext";
import MainLayout from "@/components/layouts/MainLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { format } from "date-fns";
import { Search, User, Filter } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { checkExpiredAdsStatus } from "@/contexts/data/fetchAds";

const ExpiredAds = () => {
  const { expiredAds, screens } = useData();
  const [searchTerm, setSearchTerm] = useState("");
  
  useEffect(() => {
    const verifyExpiredAds = async () => {
      try {
        const status = await checkExpiredAdsStatus();
        console.log("Verificación de anuncios vencidos:", status);
      } catch (error) {
        console.error("Error al verificar anuncios vencidos:", error);
      }
    };
    
    verifyExpiredAds();
  }, []);
  
  // Verificar si un anuncio está realmente vencido
  const isActuallyExpired = (endDate: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const end = new Date(endDate);
    end.setHours(0, 0, 0, 0);
    return end <= today;
  };
  
  // Filtrar anuncios vencidos según el término de búsqueda y verificar fechas
  const filteredAds = (searchTerm
    ? expiredAds.filter(ad => 
        ad.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ad.screens.some(screen => screen.screenName.toLowerCase().includes(searchTerm.toLowerCase())) ||
        ad.sellerName.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : expiredAds).filter(ad => isActuallyExpired(ad.endDate));
    
  console.log("Anuncios vencidos filtrados:", filteredAds.map(ad => ({
    cliente: ad.clientName,
    fechaFin: ad.endDate,
    vencido: isActuallyExpired(ad.endDate)
  })));
  
  // Ordenar anuncios por fecha de fin (más reciente primero)
  const sortedAds = [...filteredAds].sort((a, b) => 
    new Date(b.endDate).getTime() - new Date(a.endDate).getTime()
  );

  return (
    <MainLayout>
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">Historial de Anuncios Vencidos</h1>
        </div>
        
        <Card>
          <CardHeader className="space-y-2">
            <div className="flex justify-between items-center">
              <CardTitle>Anuncios Finalizados</CardTitle>
              <div className="flex gap-2 items-center">
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar por cliente, pantalla o vendedor..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
            </div>
            <CardDescription>
              Historial completo de anuncios vencidos en el sistema, incluyendo los ocultos de las pantallas.
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            {sortedAds.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-lg text-muted-foreground">
                  {searchTerm ? "No se encontraron anuncios vencidos que coincidan con la búsqueda" : "No hay anuncios vencidos"}
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Pantalla</TableHead>
                    <TableHead>Vendedor</TableHead>
                    <TableHead>Fecha Inicio</TableHead>
                    <TableHead>Fecha Fin</TableHead>
                    <TableHead className="text-right">Monto</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Estado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedAds.map((ad) => (
                    <TableRow key={ad.id}>
                      <TableCell>{ad.clientName}</TableCell>
                      <TableCell>{ad.screens.map(screen => screen.screenName).join(", ")}</TableCell>
                      <TableCell className="flex items-center">
                        <User size={14} className="mr-1 text-muted-foreground" />
                        <span>{ad.sellerName}</span>
                      </TableCell>
                      <TableCell>{format(new Date(ad.startDate), 'dd/MM/yyyy')}</TableCell>
                      <TableCell>{format(new Date(ad.endDate), 'dd/MM/yyyy')}</TableCell>
                      <TableCell className="text-right">
                        {new Intl.NumberFormat('es-AR', {
                          style: 'currency',
                          currency: 'ARS'
                        }).format(ad.amount)}
                      </TableCell>
                      <TableCell>
                        {ad.adType === "courtesy" ? (
                          <Badge className="bg-[#ff8a33] hover:bg-[#f07c28] text-white">
                            Cortesía
                          </Badge>
                        ) : (
                          <span>Venta</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="destructive" className="bg-red-500">
                          Vencido
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
};

export default ExpiredAds;
